<i18n lang="yaml" locale="en">
  name: Name
  id: ID
  project: Project
  user: User
  actions: Actions
  choose project: Please choose a project.
  add device: Add device
  attention: Attention!
  device id copied: Device ID has been copied to clipboard.
  confirm delete device: Do you really want to delete this device?
  enter new name: Please enter the new device name.
  enter name: Please enter the device name.
  device name: Device name
  device limit reached: Maximum number of devices reached.
</i18n>

<i18n lang="yaml" locale="de">
  name: Name
  id: ID
  project: Projekt
  user: Nutzer
  actions: Aktionen
  choose project: Bitte wählen Sie ein Projekt.
  add device: Gerät hinzufügen
  attention: Achtung!
  device id copied: Geräte-ID wurde in die Zwischenablage kopiert.
  confirm delete device: Wollen Sie das Gerät wirklich löschen?
  enter new name: Bitte geben Sie den neuen Namen des Geräts an.
  enter name: Bitte geben Sie den Namen des Geräts an.
  device name: Gerätename
  device limit reached: Maximale Anzahl an Geräten erreicht.
</i18n>

<template>
  <div class="device-manager">
    <table class="device-table">
      <thead>
        <tr>
          <th>{{ $t('name') }}</th>
          <th>{{ $t('id') }}</th>
          <th>{{ $t('project') }}</th>
          <th>{{ $t('user') }}</th>
          <th>{{ $t('actions') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="device in devices"
          :key="device.id"
        >
          <td>{{ device.name }}</td>
          <td>
            {{ device.id }}
            <span
              v-clipboard:copy="device.id"
              v-clipboard:success="onCopy"
            >
              <copy
                class="icon"
                size="s"
              />
            </span>
          </td>
          <td>
            <tw-dropdown
              :options="parsedProjects"
              :value="selectedDropdownProject(device.project)"
              @input="updateDevice(device.id, {project: $event.value})"
              v-if="projects"
              :placeholder="$t('choose project')"
            />
          </td>
          <td>
            {{ device.user }}
          </td>
          <td>
            <settings
              size="s"
              @click="editDevice(device.id)"
            />
            <delete
              size="s"
              color="danger"
              @click="deleteDevice(device.id)"
            />
          </td>
        </tr>
      </tbody>
    </table>
    <tw-button
      @click="toggleNewDevicePopup()"
      icon="add-circle"
      color="main"
    >
      {{ $t('add device') }}
    </tw-button>
  </div>
</template>

<script>
import axios from 'axios'
import Delete from '@/components/icons/Delete'
import Settings from '@/components/icons/Settings'
import Copy from '@/components/icons/Copy'
import TwDropdown from '@/components/ui/TwDropdown'
import TwButton from '@/components/ui/TwButton'
import { Prompt, Success, Confirm, Error } from '@/plugins/snotify'

export default {
  name: 'DeviceManager',
  components: {
    Delete,
    Settings,
    Copy,
    TwButton,
    TwDropdown
  },
  data () {
    return {
      devices: undefined,
      projects: undefined
    }
  },
  created () {
    this.fetchDevices()
    this.fetchProjects()
  },
  computed: {
    parsedProjects () {
      return this.projects ? this.projects.map(project => {
        return {
          value: project.id,
          label: project.name
        }
      }) : undefined
    }
  },
  methods: {
    selectedDropdownProject (id) {
      return this.parsedProjects.find(p => p.value === id)
    },
    onCopy () {
      new Success(this.$t('device id copied'), 1000).open()
    },
    deleteDevice (id) {
      const requestDelete = async () => {
        await axios.delete('devices/' + id)
        this.fetchDevices()
        this.$emit('dataChange')
      }
      new Confirm(this.$t('attention'), this.$t('confirm delete device'), requestDelete).open()
    },
    editDevice (id) {
      const oldName = this.devices.find(p => p.id === id).name
      const updateDeviceName = (name) => {
        this.updateDevice(id, { name })
      }
      new Prompt(oldName, this.$t('enter new name'), updateDeviceName, oldName).open()
    },
    async fetchDevices () {
      const response = await axios.get('/devices')
      this.devices = response.data
    },
    async fetchProjects () {
      const response = await axios.get('/projects')
      this.projects = response.data
    },
    toggleNewDevicePopup () {
      new Prompt(this.$t('device name'), this.$t('enter name'), this.createDevice).open()
    },
    async createDevice (name) {
      try {
        await axios.post('/devices', { name })
        this.fetchDevices()
        this.$emit('dataChange')
      } catch (e) {
        if (e.response.status === 402) {
          new Error(this.$t('device limit reached')).open()
        } else {
          throw e
        }
      }
    },
    async updateDevice (id, device) {
      await axios.patch('devices/' + id, device)
      this.fetchDevices()
    }
  }
}
</script>
